import { motion } from "framer-motion";
import { useLang } from "@/i18n";

const EASE = [0.16, 1, 0.3, 1];

export const About = () => {
  const { lang, t } = useLang();

  return (
    <section id="profilo" className="py-20 lg:py-32 px-4 sm:px-8 lg:px-16" data-testid="about-section">
      <motion.p
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.7, ease: EASE }}
        className="font-mono2 text-xs tracking-[0.35em] text-[#C8B48D] mb-4"
      >
        {t.about.eyebrow}
      </motion.p>
      <motion.h2
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8, ease: EASE, delay: 0.1 }}
        className="font-heading text-2xl sm:text-4xl lg:text-5xl font-bold uppercase tracking-wider mb-16"
      >
        {t.about.titleA} <span className="text-stroke">{t.about.titleB}</span>
      </motion.h2>

      <div className="relative" data-testid="background-timeline">
        <div className="absolute left-[19px] sm:left-[27px] top-2 bottom-2 w-px bg-gradient-to-b from-[#C8B48D] via-[#28342C] to-[#28342C]" />
        {t.about.timeline.map((stage, i) => (
          <motion.div
            key={`${stage.n}-${lang}`}
            initial={{ opacity: 0, x: -32 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: EASE, delay: i * 0.12 }}
            className="relative pl-14 sm:pl-20 pb-14 last:pb-0"
            data-testid={`timeline-stage-${stage.n}`}
          >
            <div className="absolute left-0 top-0 w-10 h-10 sm:w-14 sm:h-14 chamfer-sm border border-[#C8B48D] bg-[#111613] flex items-center justify-center">
              <span className="font-mono2 text-xs sm:text-sm font-bold text-[#C8B48D]">{stage.n}</span>
            </div>
            <div className="font-mono2 text-[10px] tracking-[0.3em] text-[#5C6A60] uppercase mb-2 pt-1">
              {t.about.stageLabel} {stage.n} — <span className="text-[#22C55E]">{stage.period}</span>
            </div>
            <h3 className="font-heading text-xl sm:text-3xl font-extrabold uppercase tracking-wide text-[#E6ECE8] mb-4">
              {stage.title}
            </h3>
            <p className="max-w-3xl text-sm sm:text-base leading-relaxed text-[#9EACA2]">{stage.body}</p>
            <div className="mt-5 flex flex-wrap gap-2">
              {stage.tags.map((tag) => (
                <span key={tag} className="font-mono2 text-[10px] tracking-widest uppercase border border-[#28342C] text-[#C8B48D] px-3 py-1.5">
                  {tag}
                </span>
              ))}
            </div>
          </motion.div>
        ))}
      </div>

      <div id="competenze" className="mt-24 scroll-mt-24" data-testid="competencies-block">
        <motion.h3
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, ease: EASE }}
          className="font-heading text-xl sm:text-2xl font-bold uppercase tracking-wider text-[#E6ECE8]"
        >
          {t.about.compTitle}
        </motion.h3>
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, ease: EASE, delay: 0.1 }}
          className="text-sm text-[#5C6A60] mt-2 mb-8"
        >
          {t.about.compIntro}
        </motion.p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="competency-grid">
          {t.about.competencies.map((c, i) => (
            <motion.div
              key={c.t}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.6, ease: EASE, delay: (i % 3) * 0.08 }}
              className="chamfer-sm border border-[#28342C] hover:border-[#5B7052] bg-[#111613] p-5 transition-colors duration-300"
            >
              <div className="font-heading text-sm font-bold uppercase tracking-wider text-[#E6ECE8] mb-2">
                <span className="font-mono2 text-[#C8B48D] mr-2">{String(i + 1).padStart(2, "0")}</span>
                {c.t}
              </div>
              <p className="text-xs leading-relaxed text-[#5C6A60]">{c.d}</p>
            </motion.div>
          ))}
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.8, ease: EASE }}
        className="chamfer border border-[#28342C] bg-[#111613] p-8 sm:p-10 mt-16 grid grid-cols-1 lg:grid-cols-2 gap-10"
        data-testid="availability-panel"
      >
        <div>
          <div className="font-mono2 text-[10px] tracking-[0.3em] text-[#5C6A60] uppercase mb-2 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-[#22C55E] pulse-dot" /> {t.about.availTitle}
          </div>
          <p className="text-sm sm:text-base leading-relaxed text-[#9EACA2]">{t.about.availBody}</p>
          <div className="mt-5 flex flex-wrap gap-2">
            {t.about.availTags.map((tag) => (
              <span key={tag} className="font-mono2 text-[10px] tracking-widest uppercase border border-[#28342C] text-[#C8B48D] px-3 py-1.5">
                {tag}
              </span>
            ))}
          </div>
        </div>
        <div className="space-y-4 self-center" data-testid="skill-meters">
          {t.about.skills.map((s, i) => (
            <div key={s.label}>
              <div className="flex justify-between font-mono2 text-[10px] tracking-widest uppercase text-[#9EACA2] mb-1.5">
                <span>{s.label}</span>
                <span className="text-[#C8B48D]">{s.value}%</span>
              </div>
              <div className="h-1 bg-[#18201B] overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  whileInView={{ width: `${s.value}%` }}
                  viewport={{ once: true }}
                  transition={{ duration: 1.1, ease: EASE, delay: 0.15 + i * 0.1 }}
                  className="h-full bg-gradient-to-r from-[#6A835C] to-[#C8B48D]"
                />
              </div>
            </div>
          ))}
        </div>
      </motion.div>
    </section>
  );
};
